import React, { useState } from "react";
import {
  Check,
  X,
  Lock,
  Loader2,
  ChevronDown,
  RotateCcw,
  CircleDashed,
  CircleCheck,
  AlertTriangle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import betaAPI from "v2/api";
import { showToast } from "utils/toast";
import { STEP_DATA_FIELDS, formatDateTime, formatApiError } from "./constants";

// ── Per-step attempt editor ─────────────────────────────────────────
// Inline (not a modal — progressive disclosure keeps the whole checklist on
// one page). Captures the optional structured data + a note, then POSTs a
// pass/fail attempt. The parent re-renders from the returned wearable detail.

const StepEditor = ({
  bleMac,
  subStage,
  step,
  seedData,
  onLogged,
  onCancel,
}) => {
  const { t } = useTranslation();
  const fields = STEP_DATA_FIELDS[step] || [];
  const [data, setData] = useState(() => {
    const init = {};
    fields.forEach((f) => {
      init[f.key] = seedData?.[f.key] != null ? String(seedData[f.key]) : "";
    });
    return init;
  });
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(null); // "pass" | "fail" | null

  const submit = async (result) => {
    setSubmitting(result);
    // Only send keys that were filled; coerce declared numbers.
    const payload = {};
    fields.forEach((f) => {
      const raw = data[f.key]?.trim?.() ?? "";
      if (raw === "") return;
      payload[f.key] =
        f.type === "number" && !Number.isNaN(Number(raw)) ? Number(raw) : raw;
    });
    try {
      const res = await betaAPI.createWearableAttempt({
        bleMac,
        body: {
          sub_stage: subStage,
          step,
          result,
          ...(Object.keys(payload).length ? { data: payload } : {}),
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        },
      });
      onLogged(res.data, result);
    } catch (err) {
      showToast.error(
        formatApiError(err, t("admin.manufacturing.validation.logFailed"))
      );
      setSubmitting(null);
    }
  };

  const inputCls =
    "w-full rounded-lg border border-[#E5E7EB] px-3 py-2 text-[13px] text-[#111827] placeholder-[#9CA3AF] outline-none transition-colors duration-150 focus:border-[#E07A3A]";

  return (
    <div className="mt-2 rounded-lg border border-[#F3F4F6] bg-[#FAFAFA] p-3">
      {fields.length > 0 && (
        <div className="mb-2 grid grid-cols-2 gap-2">
          {fields.map((f) => (
            <div
              key={f.key}
              className={fields.length === 1 ? "col-span-2" : ""}
            >
              <label className="mb-1 block text-[11px] font-medium text-[#6B7280]">
                {t(f.labelKey)}
              </label>
              <input
                type={f.type === "number" ? "number" : "text"}
                value={data[f.key]}
                onChange={(e) =>
                  setData((prev) => ({ ...prev, [f.key]: e.target.value }))
                }
                placeholder={
                  f.placeholderKey ? t(f.placeholderKey) : f.placeholder
                }
                className={inputCls}
              />
            </div>
          ))}
        </div>
      )}

      <input
        type="text"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder={t("admin.manufacturing.validation.notePlaceholder")}
        className={`${inputCls} mb-2.5`}
      />

      <div className="flex items-center gap-2">
        <button
          onClick={() => submit("pass")}
          disabled={Boolean(submitting)}
          className="flex items-center gap-1.5 rounded-lg bg-[#16A34A] px-3.5 py-2 text-[13px] font-semibold text-white transition duration-150 hover:bg-[#15803D] disabled:opacity-60"
        >
          {submitting === "pass" ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Check size={14} />
          )}
          {t("admin.manufacturing.validation.pass")}
        </button>
        <button
          onClick={() => submit("fail")}
          disabled={Boolean(submitting)}
          className="flex items-center gap-1.5 rounded-lg border border-[#FECACA] bg-white px-3.5 py-2 text-[13px] font-semibold text-[#DC2626] transition duration-150 hover:bg-[#FEF2F2] disabled:opacity-60"
        >
          {submitting === "fail" ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <X size={14} />
          )}
          {t("admin.manufacturing.validation.fail")}
        </button>
        <button
          onClick={onCancel}
          disabled={Boolean(submitting)}
          className="ml-auto text-[12px] font-medium text-[#9CA3AF] transition-colors duration-150 hover:text-[#6B7280] disabled:opacity-60"
        >
          {t("common.actions.cancel")}
        </button>
      </div>
    </div>
  );
};

// ── One step row ────────────────────────────────────────────────────

const STEP_ICON = {
  passed: { Icon: CircleCheck, color: "#16A34A" },
  failed: { Icon: AlertTriangle, color: "#DC2626" },
  pending: { Icon: CircleDashed, color: "#D1D5DB" },
};

const StepRow = ({ bleMac, subStage, step, interactive, onUpdated }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const { Icon, color } = STEP_ICON[step.status] || STEP_ICON.pending;
  const attempt = step.latest_attempt;

  const handleLogged = (detail, result) => {
    setEditing(false);
    if (result === "pass")
      showToast.success(
        t("admin.manufacturing.validation.stepPassed", { label: step.label })
      );
    onUpdated(detail);
  };

  return (
    <div className="border-t border-[#F3F4F6] px-4 py-2.5 first:border-t-0">
      <div className="flex items-center gap-3">
        <Icon size={16} style={{ color }} className="flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-medium text-[#111827]">{step.label}</p>
          {attempt && (
            <p className="truncate text-[11px] text-[#9CA3AF]">
              {attempt.result === "pass"
                ? t("common.status.passed")
                : t("common.status.failed")}
              {attempt.operator_name ? ` · ${attempt.operator_name}` : ""}
              {attempt.created_at
                ? ` · ${formatDateTime(attempt.created_at)}`
                : ""}
            </p>
          )}
        </div>

        {interactive && !editing && (
          <button
            onClick={() => setEditing(true)}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-medium transition duration-150 ${
              step.status === "pending"
                ? "text-white"
                : "border border-[#E5E7EB] bg-white text-[#374151] hover:bg-[#F9FAFB]"
            }`}
            style={
              step.status === "pending"
                ? {
                    background:
                      "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
                  }
                : undefined
            }
          >
            {step.status === "pending" ? (
              t("admin.manufacturing.validation.logResult")
            ) : (
              <>
                <RotateCcw size={12} />
                {t("admin.manufacturing.validation.retest")}
              </>
            )}
          </button>
        )}
      </div>

      <AnimatePresence initial={false}>
        {editing && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <StepEditor
              bleMac={bleMac}
              subStage={subStage}
              step={step.step}
              seedData={attempt?.data}
              onLogged={handleLogged}
              onCancel={() => setEditing(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ── One stage section ───────────────────────────────────────────────

const StageSection = ({ bleMac, index, stage, onUpdated }) => {
  const { t } = useTranslation();
  const isActive = stage.status === "active";
  const isComplete = stage.status === "complete";
  const isLocked = stage.status === "locked";
  // Complete stages collapse; active is always open; locked can't open.
  const [open, setOpen] = useState(isActive);

  const passed = stage.steps.filter((s) => s.status === "passed").length;
  const total = stage.steps.length;
  const expandable = !isLocked;
  const showSteps = isActive || open;

  const numberBadge = isComplete ? (
    <Check size={13} className="text-white" />
  ) : isLocked ? (
    <Lock size={12} className="text-[#9CA3AF]" />
  ) : (
    <span className="text-[12px] font-semibold text-white">{index + 1}</span>
  );

  return (
    <div
      className={`overflow-hidden rounded-xl border ${
        isActive ? "border-[#E07A3A]/40" : "border-[#F3F4F6]"
      } bg-white`}
    >
      <button
        onClick={() => expandable && setOpen((v) => !v)}
        disabled={!expandable}
        className={`flex w-full items-center gap-3 px-4 py-3 text-left ${
          isActive ? "bg-[#FFF9F5]" : ""
        } ${
          expandable
            ? "transition-colors duration-150 hover:bg-[#FAFAFA]"
            : "cursor-default"
        }`}
      >
        <span
          className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full"
          style={{
            backgroundColor: isComplete
              ? "#16A34A"
              : isActive
              ? "#E07A3A"
              : "#F3F4F6",
          }}
        >
          {numberBadge}
        </span>
        <div className="min-w-0 flex-1">
          <p
            className={`text-[13px] font-semibold ${
              isLocked ? "text-[#9CA3AF]" : "text-[#111827]"
            }`}
          >
            {stage.label}
          </p>
        </div>
        {isActive && (
          <span className="rounded-full bg-[#FEF3E7] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#E07A3A]">
            {t("admin.manufacturing.validation.current")}
          </span>
        )}
        <span className="text-[11px] tabular-nums text-[#9CA3AF]">
          {passed}/{total}
        </span>
        {expandable && (
          <ChevronDown
            size={15}
            className={`text-[#9CA3AF] transition-transform duration-200 ${
              showSteps ? "rotate-180" : ""
            }`}
          />
        )}
      </button>

      {showSteps && (
        <div>
          {stage.steps.map((step) => (
            <StepRow
              key={step.step}
              bleMac={bleMac}
              subStage={stage.sub_stage}
              step={step}
              interactive={isActive}
              onUpdated={onUpdated}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── The flow ────────────────────────────────────────────────────────

const ValidationFlow = ({ wearable, onUpdated }) => {
  const { t } = useTranslation();
  const validation = wearable.validation;
  const stages = validation?.stages || [];
  const isComplete = validation?.current_stage === "complete";
  // More than one stage can be active at once (centrifuge + drop share an order
  // group) — both are open and loggable; the order is up to the operator.
  const activeStages = stages.filter((s) => s.status === "active");

  if (!stages.length) {
    return (
      <div className="rounded-xl border border-[#F3F4F6] bg-white p-5 text-center text-sm text-[#6B7280]">
        {t("admin.manufacturing.validation.noFlow")}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-[#111827]">
          {t("admin.manufacturing.validation.title")}
        </h2>
        {isComplete && (
          <span className="flex items-center gap-1.5 rounded-full bg-[#DCFCE7] px-2.5 py-1 text-[11px] font-semibold text-[#16A34A]">
            <CircleCheck size={13} />
            {t("admin.manufacturing.validation.complete")}
          </span>
        )}
      </div>

      {activeStages.length > 1 && (
        <p className="mb-2.5 rounded-lg bg-[#FFF9F5] px-3 py-2 text-[12px] text-[#B45309]">
          {t("admin.manufacturing.validation.eitherOrder", {
            stages: activeStages
              .map((s) => s.label || s.sub_stage)
              .join(t("admin.manufacturing.validation.stageJoiner")),
          })}
        </p>
      )}

      <div className="flex flex-col gap-2.5">
        {stages.map((stage, i) => (
          <StageSection
            key={stage.sub_stage}
            index={i}
            stage={stage}
            bleMac={wearable.ble_mac}
            onUpdated={onUpdated}
          />
        ))}
      </div>
    </div>
  );
};

export default ValidationFlow;
